import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  BuyerListQuery,
  BuyerListsQuery,
  UpdateBuyerListInput,
  UpdateBuyerListMutation,
} from "lib/generated/app-service-gql/graphql";
import { EventNames, useTracking } from "lib/tracking";
import { useGraphQlMutation } from "../useGraphQlClient";

const updateBuyerListMutation = graphql(`
  mutation updateBuyerList($input: UpdateBuyerListInput!) {
    updateBuyerList(input: $input) {
      id
      name
      companyId
      ownerId
      sharingType
      createdAt
      updatedAt
      isDeleted
    }
  }
`);

export function useUpdateBuyerList(
  options?: UseMutationOptions<UpdateBuyerListMutation, unknown, UpdateBuyerListInput, unknown>,
) {
  const queryClient = useQueryClient();
  const { logEvent } = useTracking();

  const {
    mutate: innerMutate,
    data,
    ...rest
  } = useGraphQlMutation(updateBuyerListMutation, {
    onSuccess: (data, variables, context) => {
      const updated = data.updateBuyerList;

      // Update the single-list cache directly — preserves entries + permissionActions
      queryClient.setQueryData<BuyerListQuery>(["buyerList", variables.input.id], (old) => {
        if (!old?.buyerList) return old;
        return { buyerList: { ...old.buyerList, ...updated } };
      });

      // Update the all-lists cache directly
      queryClient.setQueryData<BuyerListsQuery>(["buyerLists"], (old) => {
        if (!old) return old;
        return {
          buyerLists: old.buyerLists.map((l) =>
            l.id === updated.id ? { ...l, name: updated.name } : l,
          ),
        };
      });

      // Invalidate so per-buyer savedLists data in tables/notice reflects the new name
      void queryClient.invalidateQueries(["searchOrganisationsViaOs"]);
      void queryClient.invalidateQueries(["supplierStatsOrganisation"]);
      void queryClient.invalidateQueries(["buyers", "buyersForSupplier"]);
      logEvent(EventNames.buyerListRenamed, {
        "List id": variables.input.id,
        "New list name": variables.input.name,
        "Context source": "saved-buyers",
      });
      options?.onSuccess?.(data, variables.input, context);
    },
  });

  return {
    ...rest,
    data: data?.updateBuyerList,
    mutate: (input: UpdateBuyerListInput) => innerMutate({ input }),
  };
}
